import axios from 'axios';
import { captureException } from '@sentry/react';

import pushEvent, {
  positionGTMEnum,
  EVENT_COMMON_ADD_TO_CART,
  EVENT_COMMON_SWITCH_TO_CARD,
  EVENT_VIEW_RECENTLY,
  EVENT_VIEW_RECENTLY_VIEWED,
  EVENT_VIEW_RECENTLY_SWITCHED,
} from 'pushEvent';
import { remainsNumToText } from 'pushEvent/helpers';
import { addProductToBasket } from 'store/common/basket/actions';
import { getCurrentFederalRegionName, getCurrentRegionName } from 'store/common/settings/selectors';
import { getCurrentCategoriesPathYM } from 'store/common/navigation/selectors';

import {
  getRecentlyViewedProductShowIndex,
  getRecentlyViewedProductsData,
  getRecentlyViewedProductsCookieMaxCount,
  getRecentlyViewedProductsCookieName,
  getRecentlyViewedProductsCookieDuration,
} from './selectors';
import {
  RECENTLY_VIEWED_CONTAINER_ID,
  INCREMENT_VIEWED_PRODUCTS,
  HAS_VIEWED_PRODUCTS,
} from './constants';
import addInLocalStorageRecentlyProducts from '../../../utils/addInLocalStorageRecentlyProducts';
import { getProductId } from '../../ProductDetails/selectors';
import addInCookiesRecentlyProducts from '../../../utils/addInCookiesRecentlyProducts';

/**
 * method for adding RecentlyViewed to basket,
 * if item is already added, moves to basket instead
 */

export const hasViewedProduct = (payload) => ({
  type: HAS_VIEWED_PRODUCTS,
  payload,
});

export const postViewedProduct = (isForceEnabled) => (dispatch, getState) => {
  const state = getState();

  const recentlyViewedProductsCookieName = getRecentlyViewedProductsCookieName(state);
  const recentlyViewedProductsCookieDuration = getRecentlyViewedProductsCookieDuration(state);
  const recentlyViewedProductsCookieMaxCount = getRecentlyViewedProductsCookieMaxCount(state);
  const productId = getProductId(state);

  addInLocalStorageRecentlyProducts(
    recentlyViewedProductsCookieName,
    productId,
    recentlyViewedProductsCookieMaxCount,
    recentlyViewedProductsCookieDuration,
  );

  addInCookiesRecentlyProducts(recentlyViewedProductsCookieName);

  const { isEnabled, data } = getRecentlyViewedProductsData(state);

  // for comparison page where there are no data
  const url =
    getRecentlyViewedProductsData(state).url || '/shop/getproductdetailslastviewedproducts/';

  const sendOption = {
    method: 'POST',
    url,
    data,
    withCredentials: true,
  };

  if (isEnabled || isForceEnabled) {
    axios(sendOption)
      .then((response) => dispatch(hasViewedProduct(response.data)))
      .catch(captureException);
  }
};

export const addToBasketRecentlyViewed =
  ({
    isInBasket,
    id,
    title,
    price,
    brand,
    src,
    /** fields required by gtm */
    position,
    discount,
    remains,
    article,
    collectionNameSingular,
  }) =>
  (dispatch) => {
    dispatch(
      addProductToBasket({
        isInBasket,
        productId: id,
        forceRedirect: true,
        source: src,
      }),
    );

    pushEvent(EVENT_COMMON_ADD_TO_CART, {
      list: positionGTMEnum.WIDGET_RECENTLY_VIEWED,
      brand,
      category: collectionNameSingular?.toLowerCase(),
      id,
      article,
      name: title,
      position,
      price: price.value,
      metric14: discount,
      /**
       * У недавно просмотренных нет ни рейтинга, ни бэйджей, ни контент-промоушенов,
       * товары в статусах `предзаказ` и `нет в наличии` не отображаются
       */
      dimension16: remainsNumToText(remains),
      // dimension17: contentPromotion,
      // metric15: rating,
      // dimension15: badges,
    });
  };

/**
 * method tracking clicks on RecentlyViewed,
 * if item is already added, moves to basket instead
 */
export const switchToRecentlyViewedCard =
  ({
    productId,
    title,
    price,
    brand,
    categoryId,
    url,
    /** fields required by gtm */
    position,
    discount,
  }) =>
  () => {
    pushEvent(EVENT_COMMON_SWITCH_TO_CARD, {
      list: positionGTMEnum.WIDGET_RECENTLY_VIEWED,
      brand,
      category: categoryId,
      id: productId,
      name: title,
      position,
      price: price.value,
      metric3: discount,
      /**
       * У недавно просмотренных нет ни рейтинга, ни бэйджей, ни контент-промоушенов,
       * товары в статусах `предзаказ` и `нет в наличии` не отображаются
       */
      dimension1: 1,
      // dimension2: contentPromotion,
      // metric4: rating,
      // dimension15: bades,
    });
    /** apply redirect */
    window.location.href = url;
  };

/**
 * onLoad event for cross sell
 */
export const recentlyViewedDOMLoaded = (list) => (dispatch, getState) => {
  pushEvent(EVENT_VIEW_RECENTLY, {
    list,
  });

  // GTM push when crossSale component appears in the viewport
  const nodeAppearCheck = (node) => {
    if (node) {
      const nodeRect = node.getBoundingClientRect();
      const nodeOffsetTop = nodeRect.top + window.pageYOffset;
      const windowScrollBottom =
        window.pageYOffset + document.documentElement.clientHeight - nodeRect.height;

      if (windowScrollBottom >= nodeOffsetTop) {
        return true;
      }
    }
    return false;
  };

  const crossSale = document.getElementById(RECENTLY_VIEWED_CONTAINER_ID);
  // ===
  const state = getState();
  const regionName = getCurrentRegionName(state);
  const federalRegionName = getCurrentFederalRegionName(state);
  const category = getCurrentCategoriesPathYM(state);
  // ===
  const crossSaleAppearListener = () => {
    if (nodeAppearCheck(crossSale)) {
      pushEvent(EVENT_VIEW_RECENTLY_VIEWED, {
        list,
        regionName,
        federalRegionName,
        category,
      });
      window.removeEventListener('scroll', crossSaleAppearListener);
    }
  };

  window.addEventListener('scroll', crossSaleAppearListener);
};

/**
 * onFrameSwitch event for cross sell
 */
export const recentlyViewedSwitch = (list, count, framesPerSlide) => (dispatch, getState) => {
  const state = getState();
  const currentShowIndex = getRecentlyViewedProductShowIndex(state);
  const regionName = getCurrentRegionName(state);
  const federalRegionName = getCurrentFederalRegionName(state);
  const category = getCurrentCategoriesPathYM(state);

  if (count > currentShowIndex) {
    dispatch({
      type: INCREMENT_VIEWED_PRODUCTS,
      nextShowIndex: count,
    });
    const newShownProductIndex = count + framesPerSlide - 1;
    pushEvent(EVENT_VIEW_RECENTLY_SWITCHED, {
      list,
      regionName,
      federalRegionName,
      newShownProductIndex,
      category,
    });
  }
};
